/**
 * Hook: Speichert ein einzelnes Content-Block-Feld (eine Sprache).
 *
 * Kapselt die Save-Logik des Feldlisten-Editors (C1): Sprach-Merge in die
 * Block-Struktur + `useUpdateContentBlock` (neue Version) + JTL-Push bei
 * Channel-Scope (Reuse des `ContentBlockCard`-Pfads). Produkt-Scope pusht nicht.
 *
 * Platzhalter-Block (block.id === ''): noch nicht angelegtes Standardfeld. Der
 * erste Save ERZEUGT den Block — im Channel-Scope legt ihn der JTL-Push
 * serverseitig (get-or-create) an, im Produkt-Scope via createContentBlock.
 */

import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useUpdateContentBlock } from '@/hooks/useContentBlock'
import { createContentBlock } from '@/lib/api/content-blocks'
import { pushDescriptionToJTL, toDescriptionBlockType } from '@/lib/api/descriptions'
import { mergeLangText } from '@/components/products/v2/tabs/content/channels/contentBlockLang'
import type { ContentBlock } from '@/types/content-blocks'

interface SaveArgs {
  productId: string
  /** null = Produkt-Scope (kein JTL-Push) */
  channelPackageId: string | null
  isJTLChannel: boolean
  lang: string
  text: string
}

export function useContentBlockFieldSave(block: ContentBlock) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const updateMutation = useUpdateContentBlock(block.id)
  const [isPushing, setIsPushing] = useState(false)
  const [isCreating, setIsCreating] = useState(false)

  const save = useCallback(
    async ({ productId, channelPackageId, isJTLChannel, lang, text }: SaveArgs): Promise<boolean> => {
      const merged = mergeLangText(block.current_version?.content, lang, text)
      const isNew = !block.id

      if (channelPackageId && isJTLChannel) {
        const jtlType = toDescriptionBlockType(block.block_type)
        if (jtlType) {
          setIsPushing(true)
          try {
            const res = await pushDescriptionToJTL(productId, {
              sales_channel_id: channelPackageId,
              language_iso: lang,
              block_type: jtlType,
              content: text,
            })
            if (!res.success) {
              toast.error(t('toast.error.jtlPush'), { description: res.error })
              return false
            }
          } catch (error) {
            toast.error(t('toast.error.jtlPush'), {
              description: error instanceof Error ? error.message : t('errors.generic'),
            })
            return false
          } finally {
            setIsPushing(false)
          }
        }
      }

      // Platzhalter (noch kein Block): anlegen statt updaten.
      if (isNew) {
        // Channel-Scope: der JTL-Push hat den Block serverseitig via
        // get-or-create bereits erzeugt — nur noch im Produkt-Scope explizit anlegen.
        if (!channelPackageId) {
          setIsCreating(true)
          try {
            const res = await createContentBlock({
              scope_type: 'product',
              scope_key: productId,
              block_type: block.block_type,
              content: merged,
            })
            if (!res.success) {
              toast.error(res.error || t('toast.error.creating'))
              return false
            }
          } catch (error) {
            toast.error(error instanceof Error ? error.message : t('toast.error.creating'))
            return false
          } finally {
            setIsCreating(false)
          }
        }
        queryClient.invalidateQueries({ queryKey: ['content-blocks'] })
        return true
      }

      updateMutation.mutate({ content: merged, publish: true })
      return true
    },
    [block, updateMutation, queryClient, t],
  )

  return { save, isSaving: updateMutation.isPending || isPushing || isCreating }
}
